import { isPathnameRestricted } from "@app/authorization";
import { ExportType } from "@app/exports/enumerations";
import { AccuracyDetail } from "@app/projects/network-assessment/accuracy";
import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { AccuracyAggregateInspectionResultGroup, NumericResultType } from "@app/projects/results/accuracy";
import { ResultRegion } from "@app/projects/results/common";
import { RegionGranularity } from "@app/region-sets";
import { LoadingSpinner, TooltipWrapper } from "@app/shared";
import Pie from "@app/shared/pie";
import { ProgressBarBand } from "@app/shared/progressBarBand";
import { AccuracyTemplate, AccuracyTemplateContext } from "@app/templates";
import { UserContextProps, withUserContext } from "@app/user";
import { flatMap, isEqual, sortBy } from "lodash";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { AccuracyTooltipLegend } from ".";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { ColumnConfig, ColumnGroup, SortDirections, TextAlignment, Waffles } from "@app/shared/waffles";
import { WafflesPaginationProps } from "@app/shared/waffles/exports";
import { AccuracyTemplateInspection, AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand } from "@app/templates/accuracy/models";
import { FilterType, ProjectPerspective } from "@app/projects/enumerators";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import InformationalIndicator from ".././InformationalSpecialtyGroupIndicator";
import StateName from "../stateName";
import { formatHelper } from "../formatHelper";
import { AttestationRateDays } from "@app/projects/enumerators";
import { Features } from "@app/feature-flags";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { filterPractitionerAtLocationConfidenceInspectionGroup } from "./AccuracyPractitionerAtLocationConfidenceFilter";
import { filterProviderClaimsActivityInspectionGroup } from "./AccuracyProviderClaimsActivityFilter";
import { filterCertifiedSkilledMedicalInterpreterInspectionGroup } from "./AccuracyCertifiedSkilledMedicalInterpreterFilter";
import { filterGenderAffirmingCareInspectionGroup } from "./AccuracyGenderAffirmingCareFilter";
import { JSX } from "react";

type AccuracyDetailsProps = RouteComponentProps<unknown, unknown, ProjectRouteParams> & CountyProjectContextProps & UserContextProps & FeatureFlagContextProps & {
  exportEndpoint: string;
};

interface AccuracyDetailsState {
  exportNetworkProviderCount: number;
  loading: boolean;
  selectedSortingColor: string;
  skip: number;
  sortDirection: SortDirection;
  sortedColumn: keyof AccuracyDetailsMultiRegionWaffleToppings;
  sortingColorMap: Map<string, string[]>;
  sortedRecords: AccuracyDetail[];
  totalAggregateCount: number;
}

class AccuracyDetailsMultiRegionWaffleToppings {
  public accuracyIndex: React.ReactNode;
  public displayText: React.ReactNode;
  public key: string;
  public parentDisplayName: React.ReactNode;
  public providerCount: React.ReactNode;
  public recentlyAttestedPercent: React.ReactNode;
  public specialtyGroupDisplayName: React.ReactNode;

  constructor(key: string, parentDisplayName: React.ReactNode, displayText: React.ReactNode, specialtyGroupDisplayName: React.ReactNode, providerCount: React.ReactNode, accuracyIndex: React.ReactNode, inspections: InspectionNode[], recentlyAttestedPercent: React.ReactNode) {
    /* eslint-disable sort-keys */
    this.key = key;
    this.parentDisplayName = parentDisplayName;
    this.displayText = displayText;
    this.specialtyGroupDisplayName = specialtyGroupDisplayName;
    this.providerCount = providerCount;
    this.accuracyIndex = accuracyIndex;

    for (const inspection of inspections) {
      Object.assign(this, { [inspection.inspectionUID]: inspection.element });
    }

    this.recentlyAttestedPercent = recentlyAttestedPercent;
    /* eslint-enable sort-keys */
  }
}

class InspectionNode {
  public element: React.ReactNode;
  public inspectionUID: string;

  constructor(inspectionUID: string, element: React.ReactNode) {
    this.element = element;
    this.inspectionUID = inspectionUID;
  }
}

class AccuracyDetailsMultiRegion extends React.Component<AccuracyDetailsProps, AccuracyDetailsState> {
  private bands: AccuracyTemplateScoringBand[] = [];
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
  private recordsPerPage = 100;

  constructor(props: AccuracyDetailsProps) {
    super(props);

    this.state = {
      exportNetworkProviderCount: 0,
      loading: true,
      selectedSortingColor: null,
      skip: 0,
      sortDirection: null,
      sortedColumn: null,
      sortedRecords: [],
      sortingColorMap: new Map<string, string[]>(),
      totalAggregateCount: 0,
    };
  }

  public async componentDidMount() {
    await this.fetchRecords();

    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }
  }

  public async componentDidUpdate(prevProps: AccuracyDetailsProps) {
    if (!isEqual(prevProps.regionFilters, this.props.regionFilters)) {
      await this.fetchRecords();

      this.setState({
        selectedSortingColor: null,
      });
    }
  }

  public render() {
    const haveResultsToRender = !this.props.loadingResults && !!this.props.runConfiguration && !!this.state.sortedRecords;
    let countyRegionCode: string = null;
    let stateRegionCode: string = null;
    let specialtyGroupCode: string = null;

    if (this.props.regionFilters) {
      countyRegionCode = this.props.regionFilters.county;
      stateRegionCode = this.props.regionFilters.state;
      specialtyGroupCode = this.props.regionFilters.specialty;
    }

    const isLowestGranularity = !!countyRegionCode && !!specialtyGroupCode;

    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <>
            {!haveResultsToRender &&
              <div className="text-center">
                <LoadingSpinner />
              </div>
            }
            {(haveResultsToRender || null) && (this.renderResults(isLowestGranularity, countyRegionCode, stateRegionCode, specialtyGroupCode, accuracyTemplate))}
          </>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private renderResults = (isLowestGranularity: boolean, countyRegionCode: string, stateRegionCode: string, specialtyGroupCode: string, accuracyTemplate: AccuracyTemplate) => {
    this.storeInspectionsAndBands(accuracyTemplate);

    const county = this.props.runConfiguration.getCounty(countyRegionCode);
    const state = this.props.runConfiguration.getState(stateRegionCode);

    const paginationProps: WafflesPaginationProps = {
      changePage: (newPageNumber) => {
        this.setState({
          loading: true,
        });
        this.onPageChange(newPageNumber);
      },
      recordsPerPage: this.recordsPerPage,
      skip: this.state.skip,
      totalRecords: this.state.totalAggregateCount,
    };

    return (
      <>
        <div className="container-fluid px-0">
          <div className="row">
            <div className="col">
              <p>Displaying {this.state.sortedRecords.length} of {this.state.totalAggregateCount} rows</p>
            </div>
          </div>

          <div className="row">
            <div className="col">
              <Waffles<AccuracyDetailsMultiRegionWaffleToppings>
                bulkActionBar={this.getBulkActionBar(county, state, specialtyGroupCode)}
                columns={this.getColumnConfig(accuracyTemplate, isLowestGranularity)}
                columnGroups={this.getColumnGroups(this.props.attestationRateDays)}
                customSort={this.customSort}
                data={this.buildWaffleToppings() || []}
                enablePagination={true}
                fullWidth={true}
                loading={this.state.loading}
                paginationProps={paginationProps}
                passThroughSorting={true}
                showCheckboxes={false}
                testId="accuracy-details-multi-region"
                useVerticalHeaders={true}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  private fetchRecords = async (isSorting?: boolean, sortedColumn: keyof AccuracyDetailsMultiRegionWaffleToppings = this.state.sortedColumn, sortDirection: SortDirection = this.state.sortDirection, newPageNumber?: number) => {
    if (!this.props.runConfiguration) {
      return;
    }
    const runConfiguration = this.props.runConfiguration;
    const regionFilters = this.props.regionFilters;

    let resultRegions = runConfiguration.states();

    if (regionFilters.state) {
      const state = runConfiguration.getState(regionFilters.state);
      resultRegions = state ? [state] : [];
    }
    if (regionFilters.county) {
      const county = runConfiguration.getCounty(regionFilters.county);
      resultRegions = county ? [county] : [];
    }

    // Track the parents of regions so we can print state names in the table
    const parentRegions = new Map<string, StateName>();

    // We decided we always want the county-level granularity
    const regions: ResultRegion[] = [];
    resultRegions.forEach((region) => {
      if (region.regionGranularity === RegionGranularity.County) {
        regions.push(region);
      } else if (region.regionGranularity === RegionGranularity.State) {
        region.subRegions.forEach((subRegion) => {
          if (subRegion.regionGranularity === RegionGranularity.County) {
            parentRegions.set(subRegion.regionCode, { abbreviation: region.regionCode, displayName: region.displayName });
            regions.push(subRegion);
          }
        });
      }
    });

    let skip: number = newPageNumber ? (newPageNumber - 1) * 100 : this.state.skip;
    let take = this.recordsPerPage;
    let sortingColorMap: Map<string, string[]> = this.state.sortingColorMap;
    let sortColor: string;

    if (isSorting && sortedColumn && sortDirection) {
      // if sorting, return the same amount of records that are currently displayed,
      // without the need to skip any records
      skip = 0;
      sortColor = this.getSortingColor(sortedColumn);
      take = this.state.sortedRecords.length;
    }

    if (isSorting && (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown)) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
      sortDirection = null;
    }

    const accuracyDetailsBatches = await runConfiguration.getBatchedAccuracyDetails(regions, skip, take, sortDirection, sortedColumn, sortColor);
    let accuracyDetails: AccuracyDetail[] = [];

    accuracyDetails = accuracyDetailsBatches.accuracyDetails;
    const totalAggregateCount = accuracyDetailsBatches.totalAggregateCount;

    if (accuracyDetailsBatches && accuracyDetailsBatches.sortingColorMap && Object.keys(accuracyDetailsBatches.sortingColorMap).length > 0) {
      // only write-over the sorting color map if it's available, which should only be on load
      sortingColorMap = new Map(Object.entries(accuracyDetailsBatches.sortingColorMap));
    }

    if (accuracyDetails) {
      accuracyDetails.forEach((detail) => {
        if (parentRegions.has(detail.regionCode)) {
          const parentInfo = parentRegions.get(detail.regionCode);
          detail.parentAbbreviation = parentInfo.abbreviation;
          detail.parentDisplayName = parentInfo.displayName;
        }
      });
    }

    let sortedRecords: AccuracyDetail[] = [];
    if (sortedColumn && sortDirection) {
      sortedRecords = accuracyDetails;
    } else {
      sortedRecords = sortBy(accuracyDetails, ["defaultDisplayOrder", "specialtyGroupDisplayOrder"]);
    }

    this.setState({
      exportNetworkProviderCount: accuracyDetailsBatches.totalProviderCount,
      loading: false,
      skip,
      sortDirection,
      sortedColumn,
      sortedRecords,
      sortingColorMap,
      totalAggregateCount
    });
  }

  private onPageChange = async (newPageNumber : number) => {
    await this.fetchRecords(null, this.state.sortedColumn, this.state.sortDirection, newPageNumber);
  }

  private storeInspectionsAndBands = (accuracyTemplate: AccuracyTemplate) => {
    const inspectionGroups: AccuracyTemplateInspectionGroup[] = [];
    const bands: AccuracyTemplateScoringBand[] = [];

    for (const inspectionGroup of accuracyTemplate.criteria.inspectionGroups) {
      if (!inspectionGroups.includes(inspectionGroup)) {
        inspectionGroups.push(inspectionGroup);
      }
    }

    for (const bandGroup of accuracyTemplate.scoring.bandGroups) {
      for (const band of bandGroup.bands) {
        if (!bands.includes(band)) {
          bands.push(band);
        }
      }
    }

    this.inspectionGroups = inspectionGroups;
    this.bands = bands;
  }

  private customSort = (records: AccuracyDetailsMultiRegionWaffleToppings[], column: keyof AccuracyDetailsMultiRegionWaffleToppings, direction: string) => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    this.fetchRecords(true, column, sortDirection);

    return records;
  }

  private getSortingColor = (column: keyof AccuracyDetailsMultiRegionWaffleToppings) => {
    let sortingColor: string = null;
    if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
      // if a selected sorting color exists and the last sorted column is this column,
      // use the selected sorting color stored in the state
      sortingColor = this.state.selectedSortingColor;
    } else {
      const colorMap = this.state.sortingColorMap.get(column);

      // if a sorting color hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the color map
      if (colorMap) {
        sortingColor = colorMap[0];

        this.setState({
          selectedSortingColor: sortingColor,
        });
      }
    }

    return sortingColor;
  }

  private getBulkActionBar = (countyRegion: ResultRegion, stateRegion: ResultRegion, specGroupCode: string) => {
    // values needed for export properties
    const countyRegionCode = countyRegion ? countyRegion.regionCode : null;
    const networkTypes = this.props.runConfiguration && this.props.runConfiguration.networkTypes || undefined;
    const stateRegionCode = stateRegion ? stateRegion.regionCode : null;
    const granularity = stateRegionCode || countyRegionCode ? RegionGranularity.County : RegionGranularity.State;
    const regionSetIds = countyRegion
      ? [countyRegion.regionSetId]
      : stateRegion
        ? this.props.countyRegionSetIds.filter(c => c.state === stateRegionCode).map(c => c.regionSetId)
        : this.props.countyRegionSetIds.map(id => id.regionSetId);

    const specialtyGroupCodes = specGroupCode
      ? [specGroupCode]
      : [];

    const disableRegionFilters = !!countyRegionCode;

    const availableExportTypes = [ExportType.ActionableProviders, ExportType.StatusReport];
    if (this.props.enabledFeatureFlags.has(Features.ACCURACY_INSPECTION_SUMMARY_REPORT_ENHANCEMENT))
      availableExportTypes.push(ExportType.AllInspections);

    return (
      <span className="bulk-action-group">
        <Link
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              activeModule: this.props.module,
              availableExportTypes,
              disableRegionFilters,
              exportNetworkProviderCount: this.state.exportNetworkProviderCount,
              filters: this.props.filters,
              granularity,
              networkTypes,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds,
              specialtyGroupCodes,
              stateFilters: this.props.filters.filter(f =>f.filterType == FilterType.State).map(f => f.filterValue)
            },
          }}
        >
          <i className="fa fa-cloud-download mr-1" />
          Create Report
        </Link>
      </span>
    );
  }

  private getColumnConfig = (accuracyTemplate: AccuracyTemplate, isLowestGranularity: boolean) => {
    let inspectionGroups: AccuracyTemplateInspectionGroup[] = filterProviderClaimsActivityInspectionGroup(accuracyTemplate ? accuracyTemplate.criteria.inspectionGroups : [],
      this.props.runConfiguration.excludeProviderClaimsActivityInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY));

    inspectionGroups = filterPractitionerAtLocationConfidenceInspectionGroup(
      accuracyTemplate && inspectionGroups ? inspectionGroups : [],
      this.props.runConfiguration.excludeAccuracyPractitionerAtLocationInspector || !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)
    );
    inspectionGroups = filterCertifiedSkilledMedicalInterpreterInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_CERTIFIED_SKILLED_MEDICAL_INTERPRETER));
    inspectionGroups = filterGenderAffirmingCareInspectionGroup(accuracyTemplate && inspectionGroups ? inspectionGroups : [], !this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_LOCATION_GENDER_AFFIRMING_CARE));

    const columns: ColumnConfig = {
      accuracyIndex: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "accuracyIndex",
      },
      displayText: {
        displayText: "",
        noResize: true,
        sticky: true,
        testId: "displayText",
      },
      parentDisplayName: {
        displayText: "",
        hidden: isLowestGranularity,
        noResize: true,
        testId: "parentDisplayName",
      },
      providerCount: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "providerCount",
      },
      recentlyAttestedPercent: {
        alignment: TextAlignment.Center,
        displayText: "",
        noResize: true,
        testId: "recentlyAttestedPercent",
      },
      specialtyGroupDisplayName: {
        displayText: "",
        noResize: true,
        sticky: true,
        testId: "specialtyGroupDisplayName",
      },
    };

    inspectionGroups.forEach((ig) => {
      ig.inspections.forEach((i) => {
        columns[i.uid] = {
          displayText: i.name,
          handleSpecialSort: this.setSelectedSortingColor,
          noResize: true,
          specialSort: this.createSpecialSortElement(i.uid),
          testId: i.uid
        };
      })
    });

    return columns;
  }

  private setSelectedSortingColor = (column: string) => {
    // determines the next selected sorting color on circle click
    const colorMap = this.state.sortingColorMap.get(column);

    if (colorMap) {
      if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
        const index = colorMap.indexOf(this.state.selectedSortingColor);

        if (index === colorMap.length - 1) {
          // if the currently selected sorting color is the last in the array of possible color options,
          // set the first option in said array as the next selected sorting color
          this.setState({
            selectedSortingColor: colorMap[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedSortingColor: colorMap[index + 1],
          });
        }
      } else {
        // if no color is selected or sorting is occurring in a new column,
        // set the first option in the array of possible color options as the next selected sorting color
        this.setState({
          selectedSortingColor: colorMap[0],
        });
      }
    }
  }

  private createSpecialSortElement = (column: string) => {
    const band = this.bands.find((x) => x.bandColor === this.state.selectedSortingColor);

    let tooltipText: JSX.Element;
    if (band) {
      const sortingColorMap: string[] = this.state.sortingColorMap.get(column);

      if (sortingColorMap && sortingColorMap.length > 1) {
        tooltipText = (
          <>
            Sorting By: {band.name}
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are {band.name}
          </>
        );
      }
    }

    return (
      <TooltipWrapper
        text={tooltipText}
        position="bottom"
      >
        <i
          className="fa fa-circle cursor-pointer icon-outline"
          style={{ color: this.state.selectedSortingColor }}
        />
      </TooltipWrapper>
    );
  }

  private getColumnGroups = (attestationRateDays: AttestationRateDays) => {
    const inspectionGroups: AccuracyTemplateInspectionGroup[] = this.inspectionGroups ? this.inspectionGroups : [];
    const has90DayFlag = this.props.enabledFeatureFlags.has(Features.USE_90_DAY_ATTESTATIONS);

    const columnGroups: Array<ColumnGroup<AccuracyDetailsMultiRegionWaffleToppings>> = [
      {
        children: ["parentDisplayName"],
        key: "State",
        testId: "state",
      },
      {
        children: ["displayText"],
        key: "County",
        testId: "county",
      },
      {
        children: ["specialtyGroupDisplayName"],
        key: this.props.accuracyResult.resultTypeText,
        testId: this.props.accuracyResult.resultTypeText,
      },
      {
        alignment: TextAlignment.Center,
        children: ["providerCount"],
        key: "Providers",
        testId: "providers",
      },
      {
        alignment: TextAlignment.Center,
        children: ["accuracyIndex"],
        key: "Score",
        testId: "score",
      },
      ...inspectionGroups.map((ig) => {
        return (
          {
            children: ig.inspections.map((i) => i.uid as keyof AccuracyDetailsMultiRegionWaffleToppings),
            key: ig.name,
            testId: ig.uid,
          }
        );
      }),
      {
        alignment: TextAlignment.Center,
        children: ["recentlyAttestedPercent"],
        key: "Attestation %",
        testId: "recentlyAttestedPercent",
        tooltip: has90DayFlag ? `Percentage is based on a ${attestationRateDays}-day attestation rate.` : null,
      },
    ];

    return columnGroups;
  }

  private buildWaffleToppings = () => {
    if (this.state.sortedRecords.length === 0) {
      return [];
    }

    const inspectionGroups: AccuracyTemplateInspectionGroup[] = this.inspectionGroups;

    // Only show informational specialty groups if:
    // a) We're looking at specialty groups.
    // b) One of the specialty groups is marked as such.
    // Otherwise, don't show the indicator, it's a waste of space.
    // NOTE: This is NOT a column as we don't want to deal with its interaction with ellipses.
    const showInformationalIndicator = this.props.perspective === ProjectPerspective.BySpecialty &&
      this.props.accuracyResult.details.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational);

    const waffleToppings: AccuracyDetailsMultiRegionWaffleToppings[] = this.state.sortedRecords.map((sr) => {
      const key: string = sr.key;

      const parentDisplayName = (
        <>
          {isPathnameRestricted(`${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`, this.props.user)
            ? <span>{sr.parentDisplayName}</span>
            : <>
              <Link
                to={{
                  pathname: `${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {sr.parentDisplayName}
              </Link>
            </>}
        </>
      );

      const displayText = (
        <>
          <InformationalIndicator visible={showInformationalIndicator} isInformational={!!sr.specialtyGroupIsInformational} />
          {isPathnameRestricted(`${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`, this.props.user)
            ? <span>{sr.displayText}</span>
            : <>
              <Link
                to={{
                  pathname: `${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {sr.displayText}
              </Link>
            </>}
        </>
      );

      const specialtyGroupDisplayName = (
        <>
          {isPathnameRestricted(`${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`, this.props.user)
            ? <span>{sr.specialtyGroupDisplayName}</span>
            : <>
              <Link
                to={{
                  pathname: `${this.props.match.url}/state/${sr.parentAbbreviation}/specialty/${sr.specialtyGroupCode}${sr.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {sr.specialtyGroupDisplayName}
              </Link>
            </>}
        </>
      );

      const providerCount = formatHelper(sr.providerCount, NumericResultType.Count);

      const accuracyIndex = formatHelper(sr.accuracyIndex, NumericResultType.Score);

      const inspections: InspectionNode[] = flatMap(inspectionGroups, (inspectionGroup) => inspectionGroup.inspections.map((inspection) => {

        const bands = this.getProgressBarBands(sr.resultGroups, inspectionGroup.name, inspection);

        return ({
          element: (
            <>
              {
                bands.length > 0 ? (
                  <div className="pie-chart-container">
                    <TooltipWrapper
                      text={
                        <AccuracyTooltipLegend
                          inspectionName={inspection.name}
                          progressBarBands={bands}
                        />
                      }
                      position="bottom"
                    >
                      <Pie segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
                    </TooltipWrapper>
                  </div>
                ) : "N/A"
              }
            </>
          ),
          inspectionUID: inspection.uid,
        });
      }));

      const has90DayFlag = this.props.enabledFeatureFlags.has(Features.USE_90_DAY_ATTESTATIONS);

      const attestationPercent = this.props.attestationRateDays === AttestationRateDays.Ninety && has90DayFlag ?
        sr.ninetyDayAttestedPercent : sr.recentlyAttestedPercent;

      const formattedAttestationPercent = formatHelper(attestationPercent, NumericResultType.Percent);

      return new AccuracyDetailsMultiRegionWaffleToppings(key, parentDisplayName, displayText, specialtyGroupDisplayName, providerCount, accuracyIndex, inspections, formattedAttestationPercent);
    });

    return waffleToppings;
  }

  private getProgressBarBands = (resultGroups: AccuracyAggregateInspectionResultGroup[], groupName: string, inspection: AccuracyTemplateInspection) => {
    const resultGroup = resultGroups.find((r) => r.groupName === groupName);
    const result = resultGroup ? resultGroup.results.find((r) => r.inspectionUid === inspection.uid) : null;
    const bandAggregates = result && result.bandAggregates || [];

    const total = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);
    return bandAggregates.map<ProgressBarBand>((a) => {
      const percent = total > 0 ? (a.providerCount / total) * 100 : 0;
      return {
        color: a.color,
        colorType: "hex",
        justification: a.justification,
        outOfScale: percent > 0 && percent < 0.1,
        percent: this.getBandPercent(percent),
        tooltip: a.name ? a.name : "",
      };
    });
  }

  // if the value is too small to render force smallest increment (0.1)
  private getBandPercent = (percent: number) => {
    return percent
      ? (percent > 0 && percent < 0.1) ? 0.3 : percent // 0.3 is the smallest visible segment
      : 0;
  }
}

const WrappedAccuracyDetails = withFeatureFlagContext(withUserContext(withCountyProjectContext(AccuracyDetailsMultiRegion)));

export { WrappedAccuracyDetails as AccuracyDetailsMultiRegion };
